import React, { useEffect, useState } from 'react';
import { PrimaryButton } from 'cdk-radial';
import { useTheme } from 'styled-components';
import {
  IAllowedIPRange,
  IAllowedIPRangeUIModel,
  IPTemplateSideSheet,
  IPTemplateUIModel
} from './IPRestrictionInterface';

import { RoundedIconButton } from "../Buttons/IconButton";
import {
  StyledInput,
  StyledTextArea,
  StyledTextButton,
  StyledActionButtonContainer,
    SideSheetDetailContainer,SideSheetHeader,SideSheetBodyContainer,
    StyledSheet,StyledCustomScrollbarWrapper
} from './StyledComponents';
import { AllowedIPInputWithHelperText } from './AllowedIPInputWithHelperText';
import { AllowedIpRangeComponent } from './AllowedIpRangeComponent';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import {
  Switch,
  SWITCH_LABEL_POSITIONS,
  IconClose
} from 'cdk-radial';

type Positions = {
  top: string;
  bottom: string;
  left: string;
  right: string;
};

type ActionButtonsContainer = {
  children: React.ReactNode;
  position?: string;
  positionProps?: Partial<Positions>;
  background?: string;
  zIndex?: string;
};

const ActionButtonContainer = ({
  children,
  position,
  positionProps,
  background,
  zIndex,
}: ActionButtonsContainer): JSX.Element => {
  return (
    <StyledActionButtonContainer
      positionProps={positionProps}
      position={position}
      background={background}
      zIndex={zIndex}
    >
      {children}
    </StyledActionButtonContainer>
  );
};

  const validateIPRange = (beginIP: string, endIP: string): boolean => {
    if (!beginIP || !endIP) return false;
    
    const ipToNumber = (ip: string): number => {
      const parts = ip.split('.').map(Number);
      return (parts[0] << 24) + (parts[1] << 16) + (parts[2] << 8) + parts[3];
    };
    
    try {
      const beginNum = ipToNumber(beginIP);
      const endNum = ipToNumber(endIP);
      return beginNum < endNum;
    } catch {
      return false;
    }
  };

  function checkForDuplicateRanges(
    ipRanges: IAllowedIPRangeUIModel[],
    t: any
  ): { hasDuplicates: boolean; updatedRanges: IAllowedIPRangeUIModel[] } {
    const seen = new Set<string>();
    let hasDuplicates = false;
  
    const updatedRanges = ipRanges.map(range => ({
      ...range,
      hasBeginError: false,
      hasEndError: false
    }));
  
    for (let i = 0; i < updatedRanges.length; i++) {
      const key = `${updatedRanges[i].begin}-${updatedRanges[i].end}`;
      if (seen.has(key)) {
        hasDuplicates = true;
        updatedRanges[i].hasBeginError = true;
        updatedRanges[i].beginErrorMsg = t('dealer-admin.ip-access.label.hasDuplicates') ;
        updatedRanges[i].endErrorMsg = t('dealer-admin.ip-access.label.hasDuplicates');
        updatedRanges[i].hasEndError = true;
        // Also mark the first occurrence
        const firstIndex = updatedRanges.findIndex(
          (r, idx) => idx < i && `${r.begin}-${r.end}` === key
        );
        if (firstIndex !== -1) {
          updatedRanges[firstIndex].hasBeginError = true;
          updatedRanges[firstIndex].hasEndError = true;
        }
      } else {
        seen.add(key);
      }
    }
  
    return { hasDuplicates, updatedRanges };
  }
  


  const IPRestrictionHeader = (props :{ title:string ,onClose : Function}) =>{
    return (<SideSheetHeader>
            <div style={{display:'flex'}}>
                <div style={{marginTop:'8px',marginLeft:'12px'}}>{props.title}</div>
            </div>
              <div>
              <RoundedIconButton
                  style={{marginRight:'12px'}}
                  dataTestId={"detail-close"}
                  darkMode
                  icon={<IconClose />}
                  onClick={props.onClose}
                  text="Close Detail"
              />
              </div> 
          </SideSheetHeader>)
  }
  

export const IPRestrictionTemplateSideSheetDetails = ({
  model,
  onSave,
  onCancel,
}: IPTemplateSideSheet): JSX.Element => {
  const { trackEvent } = useAnalytics();
  const { darkMode } = useTheme();
  const { t } = useTranslation();

  const ipRegex =
    /^((25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?))(,\s*((25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)))*$/;
  const [ipAddress, setIpAddress] = useState<string>('');
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [nameHasError, setNameHasError] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState('');
  const [name, setName] = useState('');
  const [applyByDefault,setApplyByDefault] = useState(false);
  const [id, setId] = useState('');
  const [allowedIPRangeModel, setAllowedIPRangeModel] = useState<IAllowedIPRangeUIModel[]>([]);

  useEffect(() => {
    if(model){
      setIpAddress(model.allowedIps?.join(',') || '');
      setName(model.name);
      setId(model.id);
      setApplyByDefault(model.applyByDefault);

      const allowedIpRange = model.allowedIpRange?.map(
        (ipRange: IAllowedIPRange, index: number) => {
          return {
            id: index + 1,
            begin: ipRange.begin,
            end: ipRange.end,
          } as IAllowedIPRangeUIModel;
        }
      ) || [];
      
      setAllowedIPRangeModel(allowedIpRange);
    }
  }, [model]);

  const validateIPAddresses = (ipAddressString: string): { isValid: boolean; error: string } => {
    if (!ipAddressString || ipAddressString.trim().length === 0) {
      return { isValid: true, error: '' };
    }

    const ipList = ipAddressString
      .split(',')
      .map((ip: string) => ip.trim())
      .filter((ip: string) => ip.length > 0);

    // Check for duplicates
    const uniqueIPs = new Set();
    let hasDuplicates = false;
    
    for (const ip of ipList) {
      if (uniqueIPs.has(ip)) {
        hasDuplicates = true;
        break;
      }
      uniqueIPs.add(ip);
    }

    if (hasDuplicates) {
      return { 
        isValid: false, 
        error: t('dealer-admin.ip-access.label.hasDuplicates') as string 
      };
    }

    // Validate IP format
    if (!ipRegex.test(ipAddressString)) {
      return { 
        isValid: false, 
        error: t('dealer-admin.ip-access.label.invalidIp') as string 
      };
    }

    return { isValid: true, error: '' };
  };

  const validateIPRanges = (): { isValid: boolean } => {
    if (!allowedIPRangeModel || allowedIPRangeModel.length === 0) {
      return { isValid: true };
    }
  
    const singleIpRegex = /^(25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|1?[0-9][0-9]?)$/;
  
    let isValid = true;
    const updatedModel = allowedIPRangeModel.map(range => {
      let hasBeginError = false;
      let hasEndError = false;
      let beginErrorMsg = '';
      let endErrorMsg = '';
  
      if (!range.begin && !range.end) {
        hasBeginError = true;
        hasEndError = true;
        isValid = false;
        beginErrorMsg = t('dealer-admin.ip-access.label.rangeIP');
        endErrorMsg = t('dealer-admin.ip-access.label.rangeIP');
      } else if (!range.begin || !range.end) {
        if (!range.begin) hasBeginError = true;
        if (!range.end) hasEndError = true;
        isValid = false;
        beginErrorMsg = t('dealer-admin.ip-access.label.enterIP');
        endErrorMsg = t('dealer-admin.ip-access.label.enterIP');
      } else {
        if (!singleIpRegex.test(range.begin)) {
          hasBeginError = true;
          isValid = false;
          beginErrorMsg = t('dealer-admin.ip-access.label.invalidIp');
        }
        if (!singleIpRegex.test(range.end)) {
          hasEndError = true;
          isValid = false;
          endErrorMsg = t('dealer-admin.ip-access.label.invalidIp');
        }
  
        if (!hasBeginError && !hasEndError && !validateIPRange(range.begin, range.end)) {
          hasBeginError = true;
          hasEndError = true;
          isValid = false;
          beginErrorMsg = t('dealer-admin.ip-access.label.invalidIpRange');
          endErrorMsg = t('dealer-admin.ip-access.label.invalidIpRange');
        }
      }
  
      return {
        ...range,
        hasBeginError,
        hasEndError,
        beginErrorMsg,
        endErrorMsg
      };
    });
  
    if (!isValid) {
      setAllowedIPRangeModel(updatedModel);
      return { isValid };
    }
  
    // Check for duplicate ranges (only if no field errors)
    const { hasDuplicates, updatedRanges } = checkForDuplicateRanges(updatedModel,t);
    if (hasDuplicates) {
      setAllowedIPRangeModel(updatedRanges);
      return {
        isValid: false,
      };
    }
  
    // No errors: reset any previous error flags
    const cleanedModel = updatedModel.map(r => ({
      ...r,
      hasBeginError: false,
      hasEndError: false
    }));
  
    setAllowedIPRangeModel(cleanedModel);
    return { isValid: true, };
  };


  const handleIPAddressChange = (event: any) => {
    const newIpAddress = event.target.value;
    setIpAddress(newIpAddress);
    // Clear any previous errors when user types
    setHasError(false);
    setErrorMessage('');
  };

  const handleNameChange = (event: any) => {
    const name = event.target.value;
    setName(name);
    // Clear any previous errors when user types
    setNameHasError(false);
    setNameErrorMessage('');
  };

  const handleApplyByDefaultChange = (event: any) => {
    const applyByDefault = event.target.checked;
    setApplyByDefault(applyByDefault);
  };

  const handleSave = () => {
    // Clear previous errors
    setHasError(false);
    setErrorMessage('');

    // Validate IP addresses
    const ipValidation = validateIPAddresses(ipAddress);
    
    // Validate IP ranges
    const rangeValidation = validateIPRanges();

    // Show validation errors
    if (!ipValidation.isValid) {
      setHasError(true)
      setErrorMessage(ipValidation.error);
      return;
    }

    if (name.trim().length === 0) {
      setNameHasError(true)
      setNameErrorMessage("Name is required");
      return;
    }

    if (!rangeValidation.isValid) {
      return;
    }

    // Prepare data for save
    const range = allowedIPRangeModel
      ?.filter((range: any) => range.begin && range.end) // Only include complete ranges
      ?.map((range: IAllowedIPRangeUIModel) => ({
        begin: range.begin,
        end: range.end,
      }));

    const modelObj = {
      allowedIps: ipAddress ? ipAddress.split(',').map((ip: string) => ip.trim()) : [],
      allowedIpRange: range,
      id: id,
      name: name,
      applyByDefault: applyByDefault
    } as IPTemplateUIModel;

    onSave(modelObj);
    trackEvent(AMPLITUDE_EVENTS.DEALER_ADMINISTRATION, { Action: 'IP Access Save' });
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <StyledSheet>
      <SideSheetDetailContainer data-testid="ip-restriction-detail-template" >
       <IPRestrictionHeader 
            title = {t('dealer-admin.ip-access.detail.templateTitle')}
            onClose={handleCancel}/>

    <StyledCustomScrollbarWrapper>
      <SideSheetBodyContainer>

      <div style={{display:'flex',flexDirection:'row-reverse',marginRight: '-32px'}}>
      <Switch checked={applyByDefault} label="Apply to all apps and workflows" name="apply_by_default" 
        labelPosition={SWITCH_LABEL_POSITIONS.BEFORE} 
        onChange={handleApplyByDefaultChange} />
      </div>

      <StyledInput
        dataTestId={`template`}
        id={`template-name`}
        inputDivider="left"
        label={
          "Template Name"
        }
        name={'template'}
        onChange={handleNameChange}
        value={name || ''}
        enableCustomValidation
        hasError={nameHasError}
        errorMessage={nameErrorMessage}
      />

      <div style={{ marginTop: '10px' }}>
        <div
          style={{
            fontFamily: 'Roboto,sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            marginLeft: '8px',
            marginTop: '5px',
            marginBottom: '5px',
          }}
        >
          {t('dealer-admin.ip-access.label.allowedIPAddress')}
        </div>
        <AllowedIPInputWithHelperText helperLabel={t('dealer-admin.ip-access.label.publicIpInfo')}>
        <StyledTextArea
          dataTestId={`ip-address-input`}
          errorMessage={errorMessage}
          id={`ip-address-input`}
          inputDivider="left"
          label={t('dealer-admin.ip-access.label.ipAddress')}
          name={'ipaddress'}
          onChange={handleIPAddressChange}
          placeholder={t('dealer-admin.ip-access.label.ipAddressPlaceHolder')}
          value={ipAddress || ''}
          maxLength={500}
          enableCustomValidation
          hasError={hasError}
        />
        </AllowedIPInputWithHelperText>
      </div>

      <div>
        <AllowedIpRangeComponent
          allowedIPRange={allowedIPRangeModel}
          setAllowedIPRangeModel={setAllowedIPRangeModel}
          label={t('dealer-admin.ip-access.label.allowedIPRange')}
        />
      </div>

      <ActionButtonContainer
        position="sticky"
        zIndex="999"
        positionProps={{ bottom: '0px' }}
        background={
          darkMode
            ? 'linear-gradient(black, transparent)'
            : 'linear-gradient(white, transparent)'
        }
      >
        <div>
          <StyledTextButton
            dataTestId="cancel-side-sheet-button"
            iconColor="primary"
            onClick={handleCancel}
            text={t('dealer-admin.ip-access.label.cancel')}
          />
          <PrimaryButton
            dataTestId="save-side-sheet-button"
            iconColor="primary"
            onClick={handleSave}
            text={t('dealer-admin.ip-access.label.save')}
          />
        </div>

      </ActionButtonContainer>
      </SideSheetBodyContainer>
    </StyledCustomScrollbarWrapper>
    </SideSheetDetailContainer>
    </StyledSheet>
  );
};
